import { useState, useCallback, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { SYSTEM_EVENTS_QUERY, SystemEventsQueryResult, ISystemEvents } from '../graphql'

interface UseSystemEventsResult {
  data: ISystemEvents | null
  loading: boolean
  error: Error | null
  fetchedAt: Date | null
  dataAgeSeconds: number
  isStale: boolean
  refetch: () => Promise<void>
}

const STALE_THRESHOLD_SECONDS = 60

export const useSystemEvents = (): UseSystemEventsResult => {
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null)
  const [dataAgeSeconds, setDataAgeSeconds] = useState(0)

  const {
    data,
    loading,
    error,
    refetch: apolloRefetch,
  } = useQuery<SystemEventsQueryResult>(SYSTEM_EVENTS_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setFetchedAt(new Date())
    },
  })

  // Update data age every second
  useEffect(() => {
    if (!fetchedAt) return

    const interval = setInterval(() => {
      const now = new Date()
      const ageMs = now.getTime() - fetchedAt.getTime()
      setDataAgeSeconds(Math.floor(ageMs / 1000))
    }, 1000)

    return () => clearInterval(interval)
  }, [fetchedAt])

  const refetch = useCallback(async () => {
    await apolloRefetch()
    setFetchedAt(new Date())
  }, [apolloRefetch])

  return {
    data: data?.systemEvents ?? null,
    loading,
    error: error ?? null,
    fetchedAt,
    dataAgeSeconds,
    isStale: dataAgeSeconds >= STALE_THRESHOLD_SECONDS,
    refetch,
  }
}
